import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { Asset, AssetLifecycleStatus, GeoPoint, getConditionGrade } from '@easydone/shared';
import { generateAssetQRCode, printQRCodeLabel } from './qrCodeService';

/**
 * Get all assets for an organization
 */
export async function getAssets(orgId: string): Promise<Asset[]> {
  const assetsRef = collection(db, 'organizations', orgId, 'assets');
  const q = query(assetsRef, orderBy('serialNumber', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Asset));
}

/**
 * Get assets by lifecycle status
 */
export async function getAssetsByLifecycleStatus(
  orgId: string,
  lifecycleStatus: AssetLifecycleStatus
): Promise<Asset[]> {
  const assetsRef = collection(db, 'organizations', orgId, 'assets');
  const q = query(
    assetsRef,
    where('lifecycleStatus', '==', lifecycleStatus),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Asset));
}

/**
 * Get assets currently in storage
 */
export async function getAssetsInStorage(orgId: string): Promise<Asset[]> {
  return getAssetsByLifecycleStatus(orgId, 'in_storage');
}

/**
 * Get commissioned assets (installed in FLOCs)
 */
export async function getCommissionedAssets(orgId: string): Promise<Asset[]> {
  return getAssetsByLifecycleStatus(orgId, 'commissioned');
}

/**
 * Get assets by FLOC
 */
export async function getAssetsByFloc(orgId: string, flocId: string): Promise<Asset[]> {
  const assetsRef = collection(db, 'organizations', orgId, 'assets');
  const q = query(
    assetsRef,
    where('currentFlocId', '==', flocId),
    orderBy('serialNumber', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Asset));
}

/**
 * Get a single asset by ID
 */
export async function getAsset(orgId: string, assetId: string): Promise<Asset | null> {
  const assetRef = doc(db, 'organizations', orgId, 'assets', assetId);
  const snapshot = await getDoc(assetRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Asset;
}

/**
 * Asset creation data
 */
export interface CreateAssetData {
  serialNumber: string;
  assetId: string; // User-defined asset ID/code
  name: string;
  type: string;
  category?: string;
  description?: string;
  manufacturer?: string;
  model?: string;
  yearManufactured?: number;
  purchaseDate?: Date;
  warrantyExpiryDate?: Date;
  purchaseCost?: number;
  replacementCost?: number;
  currency?: string;
  storageLocation?: string;
  initialConditionScore?: number;
  location?: GeoPoint;
  metadata?: Record<string, any>;
}

/**
 * Create a new asset (starts in storage)
 */
export async function createAsset(
  orgId: string,
  data: CreateAssetData,
  userId: string
): Promise<Asset> {
  const assetsRef = collection(db, 'organizations', orgId, 'assets');

  const initialConditionScore = data.initialConditionScore || 100;
  const conditionGrade = getConditionGrade(initialConditionScore);

  const assetData = {
    orgId,
    serialNumber: data.serialNumber,
    assetId: data.assetId,
    name: data.name,
    type: data.type,
    category: data.category || null,
    description: data.description || null,
    manufacturer: data.manufacturer || null,
    model: data.model || null,
    yearManufactured: data.yearManufactured || null,
    lifecycleStatus: 'in_storage' as AssetLifecycleStatus,
    currentFlocId: null,
    storageLocation: data.storageLocation || 'Default Storage',
    purchaseDate: data.purchaseDate ? Timestamp.fromDate(data.purchaseDate) : null,
    warrantyExpiryDate: data.warrantyExpiryDate ? Timestamp.fromDate(data.warrantyExpiryDate) : null,
    purchaseCost: data.purchaseCost || null,
    replacementCost: data.replacementCost || null,
    currency: data.currency || 'USD',
    conditionScore: initialConditionScore,
    conditionGrade: conditionGrade,
    lastConditionUpdate: serverTimestamp(),
    status: 'operational', // Legacy field
    location: data.location || null,
    qrCode: null, // Will be generated after creation
    photos: [],
    documents: [],
    metadata: data.metadata || {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
    updatedBy: userId,
  };

  const docRef = await addDoc(assetsRef, assetData);
  const snapshot = await getDoc(docRef);

  // Generate QR code
  const asset = {
    id: snapshot.id,
    ...snapshot.data(),
  } as Asset;

  const qrCodeUrl = await generateQRCode(asset);
  await updateDoc(docRef, { qrCode: qrCodeUrl });

  return {
    ...asset,
    qrCode: qrCodeUrl,
  };
}

/**
 * Create a placeholder asset for a FLOC
 * Used when inspecting FLOCs without assets and auto-create is enabled
 */
export async function createPlaceholderAsset(
  orgId: string,
  flocName: string,
  flocCode: string,
  userId: string
): Promise<Asset> {
  const assetsRef = collection(db, 'organizations', orgId, 'assets');

  const assetData = {
    orgId,
    serialNumber: `TEMP-${flocCode}`,
    assetId: `TEMP-${flocCode}`,
    name: `${flocName} (Placeholder)`,
    type: 'Equipment',
    category: null,
    description: `Auto-generated placeholder asset for FLOC ${flocCode}. Update with equipment details.`,
    manufacturer: null,
    model: null,
    purchaseDate: null,
    warrantyExpiry: null,
    installationDate: null,
    lifecycleStatus: 'in_storage' as AssetLifecycleStatus,
    currentFlocId: null,
    status: 'active',
    conditionScore: 100,
    conditionGrade: 'A',
    lastConditionUpdate: serverTimestamp(),
    photos: [],
    documents: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
    updatedBy: userId,
    autoGenerated: true, // Flag to identify auto-generated placeholders
  };

  const docRef = await addDoc(assetsRef, assetData);
  const snapshot = await getDoc(docRef);

  // Generate QR code
  const asset = {
    id: snapshot.id,
    ...snapshot.data(),
  } as Asset;

  const qrCodeUrl = await generateQRCode(asset);
  await updateDoc(docRef, { qrCode: qrCodeUrl });

  return {
    ...asset,
    qrCode: qrCodeUrl,
  };
}

/**
 * Update an asset
 */
export async function updateAsset(
  orgId: string,
  assetId: string,
  data: Partial<CreateAssetData> | Record<string, any>,
  userId: string
): Promise<void> {
  const assetRef = doc(db, 'organizations', orgId, 'assets', assetId);

  const updateData: any = {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  // Convert dates to Timestamps
  if (data.purchaseDate) {
    updateData.purchaseDate = Timestamp.fromDate(data.purchaseDate);
  }
  if (data.warrantyExpiryDate) {
    updateData.warrantyExpiryDate = Timestamp.fromDate(data.warrantyExpiryDate);
  }

  await updateDoc(assetRef, updateData);
}

/**
 * Update asset condition score
 */
export async function updateAssetCondition(
  orgId: string,
  assetId: string,
  conditionScore: number
): Promise<void> {
  const grade = getConditionGrade(conditionScore);
  const assetRef = doc(db, 'organizations', orgId, 'assets', assetId);
  await updateDoc(assetRef, {
    conditionScore,
    conditionGrade: grade,
    lastConditionUpdate: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update asset lifecycle status
 */
export async function updateAssetLifecycleStatus(
  orgId: string,
  assetId: string,
  lifecycleStatus: AssetLifecycleStatus,
  userId: string
): Promise<void> {
  const assetRef = doc(db, 'organizations', orgId, 'assets', assetId);
  await updateDoc(assetRef, {
    lifecycleStatus,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

/**
 * Delete an asset
 * Only allowed if not commissioned
 */
export async function deleteAsset(orgId: string, assetId: string): Promise<void> {
  const asset = await getAsset(orgId, assetId);

  if (!asset) {
    throw new Error('Asset not found');
  }

  if (asset.lifecycleStatus === 'commissioned') {
    throw new Error('Cannot delete commissioned asset. Decommission it first.');
  }

  const assetRef = doc(db, 'organizations', orgId, 'assets', assetId);
  await deleteDoc(assetRef);
}

/**
 * Generate QR code for an asset
 */
export async function generateQRCode(asset: Asset): Promise<string> {
  try {
    const qrCodeDataUrl = await generateAssetQRCode(
      asset.orgId,
      asset.id,
      asset.name
    );
    return qrCodeDataUrl;
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    throw error;
  }
}

/**
 * Download QR code as PNG
 */
export function downloadQRCode(qrCodeDataUrl: string, assetName: string): void {
  const link = document.createElement('a');
  link.href = qrCodeDataUrl;
  link.download = `${assetName.replace(/\s+/g, '_')}_QR.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Print QR code
 */
export function printQRCode(qrCodeDataUrl: string, assetName: string, assetId: string): void {
  printQRCodeLabel(qrCodeDataUrl, assetName, `Serial: ${assetId}`);
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use getAssetsByFloc instead
 */
export async function getSiteAssets(orgId: string, _siteId: string): Promise<Asset[]> {
  // This is now deprecated - FLOCs replace sites for asset organization
  // Return all assets for now
  console.warn('getSiteAssets is deprecated. Use getAssetsByFloc or getCommissionedAssets instead.');
  return getAssets(orgId);
}
